import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Provider, useGetCrmAgencyConnectionDetails } from '@urbanx/agx.crm.client';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { useToast } from '@urbanx/agx.ui-components2';
import CrmDisconnectedModal from './CrmDisconnectedModal';
import { CrmDisconnectedModalFields, CrmDisconnectedModalFieldValue } from './CrmDataUpdateHelper';

export interface CrmDisconnectedModalChange {
  field: CrmDisconnectedModalFields;
  value: CrmDisconnectedModalFieldValue;
}

export const useCrmDisconnectedModal = () => {
  // These routes identify the campaign's agency, not the signed-in staff member's agency.
  const { agencyId, campaignId } = useParams();
  const { pushToast } = useToast();
  const connection = useGetCrmAgencyConnectionDetails(agencyId ?? '', Boolean(agencyId));
  const activeProvider = connection.data?.activeProvider;
  const rexListing = useGetListingByCampaignId(
    campaignId ?? '',
    Boolean(agencyId && campaignId && !connection.isError && activeProvider === Provider.Rex)
  );
  const scope = `${agencyId}:${campaignId}`;
  const [changes, setChanges] = useState({
    scope,
    fields: new Map<CrmDisconnectedModalFields, CrmDisconnectedModalFieldValue>(),
  });
  const [openedFor, setOpenedFor] = useState<string>();
  if (changes.scope !== scope) {
    setChanges({ scope, fields: new Map() });
    setOpenedFor(undefined);
  }
  const changedFields = changes.fields;

  const lookupFailed =
    connection.isError || (activeProvider === Provider.Rex && rexListing.isError);
  const lookupPending =
    connection.isPending ||
    connection.isFetching ||
    (activeProvider === Provider.Rex && (rexListing.isPending || rexListing.isFetching));
  // Other providers do not receive updates from these save flows yet.
  const isDisconnected = !activeProvider || (activeProvider === Provider.Rex && !rexListing.data);
  const canOpenWarning = Boolean(
    agencyId && campaignId && !lookupFailed && !lookupPending && isDisconnected
  );
  const warningKey = `${scope}:${activeProvider ?? 'none'}`;
  if (
    openedFor &&
    !lookupFailed &&
    !lookupPending &&
    (!isDisconnected || openedFor !== warningKey)
  ) {
    setOpenedFor(undefined);
    setChanges({ scope, fields: new Map() });
  }

  const addChangedField = (
    field: CrmDisconnectedModalFields,
    value: CrmDisconnectedModalFieldValue
  ) => {
    setChanges((previous) => ({
      scope,
      fields: new Map(previous.scope === scope ? previous.fields : []).set(field, value),
    }));
  };

  const openWithChanges = (nextChanges: CrmDisconnectedModalChange[]) => {
    setOpenedFor(undefined);
    const fields = new Map(nextChanges.map(({ field, value }) => [field, value]));
    setChanges({ scope, fields: canOpenWarning ? fields : new Map() });
    if (fields.size === 0) return;
    if (lookupFailed) {
      pushToast({ type: 'error', message: 'Unable to check CRM connection' });
      return;
    }
    if (canOpenWarning) setOpenedFor(warningKey);
  };

  const openModal = (additionalChanges: CrmDisconnectedModalChange[] = []) => {
    openWithChanges([
      ...Array.from(changedFields, ([field, value]) => ({ field, value })),
      ...additionalChanges,
    ]);
  };

  const closeModal = () => {
    setOpenedFor(undefined);
    setChanges({ scope, fields: new Map() });
  };

  // Save notifications refetch these queries; keep an open warning mounted during that refresh.
  const modal =
    isDisconnected && openedFor === warningKey ? (
      <CrmDisconnectedModal
        providerName={activeProvider === Provider.Rex ? 'Rex' : undefined}
        setIsDialogOpen={closeModal}
        changedFields={changedFields}
      />
    ) : null;

  return { addChangedField, modal, openModal, openWithChanges };
};

export default useCrmDisconnectedModal;
